import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  Globe,
  KeyRound,
  Link2Off,
  MessageCircle,
  RefreshCw,
  Smartphone,
} from "lucide-react";
import type { MetaWhatsAppStatusDto, UpdateMetaWhatsAppConfigInput } from "@gatiq/shared";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  ConfirmDialog,
  Field,
  PageBody,
  Skeleton,
  Switch,
  TextInput,
  useToast,
  type Tone,
} from "@/ui";
import { api } from "@/lib/api";
import { AiSensyPanel } from "./AiSensyPanel";

/**
 * The shared WhatsApp account — super admin only.
 *
 * One number / account sends one-time codes for every organization, so pairing
 * it or configuring Meta Cloud API is a PLATFORM decision, not an org one.
 */
type WhatsappStatus = "connected" | "connecting" | "disconnected" | "unreachable";

interface Session {
  status: WhatsappStatus;
  phone: string | null;
}

const BA_VIEW: Record<WhatsappStatus, { label: string; tone: Tone; headline: string; detail: string }> = {
  connected: {
    label: "Connected",
    tone: "success",
    headline: "Linked phone ready",
    detail: "Messages assigned to this route can be sent from the linked number.",
  },
  connecting: {
    label: "Waiting for scan",
    tone: "warning",
    headline: "No phone linked yet",
    detail:
      "Scan the QR below: WhatsApp → Linked Devices → Link a device.",
  },
  disconnected: {
    label: "Not linked",
    tone: "danger",
    headline: "No phone linked",
    detail: "Messages assigned to this route need a linked phone.",
  },
  unreachable: {
    label: "Service down",
    tone: "danger",
    headline: "WhatsApp service is not responding",
    detail: "The bridge process is unreachable. Someone with server access needs to check it.",
  },
};

const QR_POLL_MS = 4000;

export function WhatsAppPanel() {
  const toast = useToast();

  /* ── Baileys state ────────────────────────────────────────────── */
  const [session, setSession] = useState<Session | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [baLoading, setBaLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const timer = useRef<number | null>(null);

  /* ── Meta state ───────────────────────────────────────────────── */
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaTesting, setMetaTesting] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [draftToken, setDraftToken] = useState("");
  const [testResult, setTestResult] = useState<{
    valid: boolean;
    verifiedName?: string;
    displayPhoneNumber?: string;
    qualityRating?: string;
    error?: string;
  } | null>(null);
  const [metaConfig, setMetaConfig] = useState<MetaWhatsAppStatusDto>({
    activeProvider: "baileys",
    enabled: false,
    phoneNumberId: "",
    wabaId: "",
    hasAccessToken: false,
    maskedAccessToken: "",
    apiVersion: "v21.0",
  });

  /* ── Fetching ─────────────────────────────────────────────────── */
  const refreshBaileys = useCallback(async (): Promise<void> => {
    try {
      const next = await api.get<Session>("/platform/whatsapp");
      setSession(next);
    } catch {
      setSession({ status: "unreachable", phone: null });
    } finally {
      setBaLoading(false);
    }
  }, []);

  const refreshMeta = useCallback(async (): Promise<void> => {
    setMetaLoading(true);
    try {
      const data = await api.get<MetaWhatsAppStatusDto>("/platform/whatsapp/meta");
      setMetaConfig(data);
    } catch {
      // Meta not configured yet — silent
    } finally {
      setMetaLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshBaileys();
    void refreshMeta();
  }, [refreshBaileys, refreshMeta]);

  /* ── QR polling ───────────────────────────────────────────────── */
  useEffect(() => {
    if (session?.status !== "connecting") {
      setQr(null);
      return;
    }
    let cancelled = false;

    const tick = async (): Promise<void> => {
      try {
        const res = await api.get<{ qr: string | null }>("/platform/whatsapp/qr");
        if (!cancelled) setQr(res.qr);
      } catch {
        // 404 = no code right now
      }
      const next = await api.get<Session>("/platform/whatsapp");
      if (!cancelled && next.status === "connecting") {
        timer.current = window.setTimeout(() => void tick(), QR_POLL_MS);
      }
      if (!cancelled) setSession(next);
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [session?.status]);

  /* ── Baileys actions ──────────────────────────────────────────── */
  const startPairing = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      setSession(await api.post<Session>("/platform/whatsapp/connect", {}));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not start pairing.");
    } finally {
      setBusy(false);
    }
  }, [toast]);

  const unlink = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      await api.del("/platform/whatsapp");
      setConfirmUnlink(false);
      toast.success("WhatsApp unlinked.");
      void refreshBaileys();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not unlink WhatsApp.");
    } finally {
      setBusy(false);
    }
  }, [toast, refreshBaileys]);

  /* ── Meta actions ─────────────────────────────────────────────── */
  const handleSaveMeta = useCallback(async (opts?: { clearAccessToken?: boolean }): Promise<void> => {
    setMetaSaving(true);
    try {
      const payload: UpdateMetaWhatsAppConfigInput = {
        activeProvider: metaConfig.activeProvider,
        phoneNumberId: metaConfig.phoneNumberId.trim(),
        wabaId: metaConfig.wabaId.trim(),
        apiVersion: metaConfig.apiVersion.trim() || "v21.0",
        clearAccessToken: opts?.clearAccessToken,
      };
      if (metaConfig.activeProvider === "meta") {
        payload.enabled = true;
      }
      if (!opts?.clearAccessToken && draftToken.trim()) {
        payload.accessToken = draftToken.trim();
      }

      const res = await api.put<MetaWhatsAppStatusDto>("/platform/whatsapp/meta", payload);
      setMetaConfig(res);
      setDraftToken("");
      toast.success("Meta WhatsApp configuration saved.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save Meta configuration.");
    } finally {
      setMetaSaving(false);
    }
  }, [metaConfig, draftToken, toast]);

  const handleTestMeta = useCallback(async (): Promise<void> => {
    if (!metaConfig.phoneNumberId.trim()) {
      toast.error("Enter a Phone Number ID.");
      return;
    }
    if (!metaConfig.hasAccessToken && !draftToken.trim()) {
      toast.error("Enter an Access Token.");
      return;
    }

    setMetaTesting(true);
    setTestResult(null);
    try {
      const res = await api.post<{
        valid: boolean;
        verifiedName?: string;
        displayPhoneNumber?: string;
        qualityRating?: string;
        error?: string;
      }>("/platform/whatsapp/meta/test", {
        phoneNumberId: metaConfig.phoneNumberId.trim(),
        accessToken: draftToken.trim() || undefined,
        apiVersion: metaConfig.apiVersion.trim() || "v21.0",
      });
      setTestResult(res);
      if (res.valid) {
        toast.success(
          `Connected! ${res.verifiedName || "Account valid"}${res.displayPhoneNumber ? ` (${res.displayPhoneNumber})` : ""}`,
        );
      } else {
        toast.error(res.error || "Meta credentials verification failed.");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Connection check failed.";
      setTestResult({ valid: false, error: msg });
      toast.error(msg);
    } finally {
      setMetaTesting(false);
    }
  }, [metaConfig, toast]);

  /* ── Derived ──────────────────────────────────────────────────── */
  const provider = metaConfig.activeProvider;
  const baView = session ? BA_VIEW[session.status] : null;
  const hasMetaCredentials = metaConfig.phoneNumberId && metaConfig.hasAccessToken;
  const isMetaConfigured = hasMetaCredentials || (metaConfig.phoneNumberId && draftToken);

  return (
    <PageBody>
      <div className="flex max-w-5xl flex-col gap-5">
        <AiSensyPanel />
        {/* ── Provider selector ─────────────────────────────────── */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-title text-lg font-semibold text-ink">Other WhatsApp routes</h2>
            <p className="text-sm text-muted">
              Select the default route for messages without an enabled AiSensy campaign.
            </p>
          </div>
          <div className="flex rounded-lg border border-rule bg-raised p-0.5">
            {(["baileys", "meta"] as const).map((p) => (
              <button
                key={p}
                className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                  provider === p
                    ? "bg-brand text-white shadow-sm"
                    : "text-muted hover:text-ink"
                }`}
                onClick={() => setMetaConfig((prev) => ({ ...prev, activeProvider: p }))}
                type="button"
              >
                {p === "baileys" ? "Linked Phone (QR)" : "Meta Cloud API"}
              </button>
            ))}
          </div>
        </div>

        {/* ── Two-column layout ─────────────────────────────────── */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* ── Meta Cloud API card ────────────────────────────── */}
          <Card>
            <CardBody className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <Globe aria-hidden="true" className="size-5 text-muted" strokeWidth={1.75} />
                  <span className="font-title text-base font-semibold text-ink">
                    Meta Cloud API
                  </span>
                </div>
                {metaLoading ? (
                  <Skeleton className="h-5 w-20" />
                ) : provider === "meta" && hasMetaCredentials ? (
                  <Badge tone="success">Active</Badge>
                ) : isMetaConfigured ? (
                  <Badge tone="neutral">Configured</Badge>
                ) : (
                  <Badge tone="neutral">Not set up</Badge>
                )}
              </div>

              {provider === "meta" && (
                <>
                  <p className="text-sm text-muted">
                    Configure your Meta WhatsApp Business account to send OTPs and
                    registration links.
                  </p>

                  <div className="flex flex-col gap-3">
                    <TextInput
                      hint="From Meta App Dashboard → WhatsApp → API Setup"
                      label="Phone Number ID"
                      onChange={(e) =>
                        setMetaConfig((prev) => ({ ...prev, phoneNumberId: e.target.value }))
                      }
                      placeholder="e.g. 105943892523456"
                      value={metaConfig.phoneNumberId}
                    />

                    <TextInput
                      hint="From Meta Business Manager"
                      label="WABA ID"
                      onChange={(e) => setMetaConfig((prev) => ({ ...prev, wabaId: e.target.value }))}
                      placeholder="e.g. 103948572910293"
                      value={metaConfig.wabaId}
                    />

                    <Field
                      hint="System User token with whatsapp_business_messaging permission"
                      label="Access Token"
                    >
                      {(id) => (
                        <div className="relative">
                          <input
                            className="h-9 w-full rounded-md border border-rule bg-raised px-3 pr-10 text-sm text-ink shadow-control placeholder:text-muted/60 transition-[border-color,box-shadow] duration-150 hover:border-ink/25 focus:border-brand focus:shadow-[0_0_0_3px_var(--gq-color-1-ring)] focus:outline-none"
                            id={id}
                            onChange={(e) =>
                              setDraftToken(e.target.value)
                            }
                            placeholder={
                              metaConfig.hasAccessToken
                                ? "Leave blank to keep current token"
                                : "Paste token here"
                            }
                            type={showToken ? "text" : "password"}
                            value={draftToken}
                          />
                          <button
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted hover:text-ink"
                            onClick={() => setShowToken((v) => !v)}
                            type="button"
                            aria-label={showToken ? "Hide token" : "Show token"}
                          >
                            {showToken ? (
                              <EyeOff className="size-4" />
                            ) : (
                              <Eye className="size-4" />
                            )}
                          </button>
                        </div>
                      )}
                    </Field>

                    {metaConfig.hasAccessToken && metaConfig.maskedAccessToken && (
                      <div className="flex items-center justify-between rounded-md bg-surface px-3 py-2 text-xs">
                        <span className="text-muted">
                          Current token:{" "}
                          <span className="font-mono text-ink">{metaConfig.maskedAccessToken}</span>
                        </span>
                        <button
                          className="font-medium text-danger hover:underline"
                          onClick={() => {
                            setDraftToken("");
                            void handleSaveMeta({ clearAccessToken: true });
                          }}
                          type="button"
                        >
                          Clear
                        </button>
                      </div>
                    )}

                    <TextInput
                      hint="Default: v21.0"
                      label="API Version"
                      onChange={(e) =>
                        setMetaConfig((prev) => ({ ...prev, apiVersion: e.target.value }))
                      }
                      placeholder="v21.0"
                      value={metaConfig.apiVersion}
                    />
                  </div>

                  {/* Test result */}
                  {testResult && (
                    <div
                      className={`flex items-start gap-2.5 rounded-lg border p-3 text-sm ${
                        testResult.valid
                          ? "border-success/30 bg-success/5 text-ink"
                          : "border-danger/30 bg-danger/5 text-ink"
                      }`}
                    >
                      {testResult.valid ? (
                        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
                      ) : (
                        <AlertCircle className="mt-0.5 size-4 shrink-0 text-danger" />
                      )}
                      <div>
                        <p className="font-medium">
                          {testResult.valid ? "Connection verified" : "Connection failed"}
                        </p>
                        {testResult.valid ? (
                          <p className="mt-0.5 text-xs text-muted">
                            {testResult.verifiedName && `${testResult.verifiedName}`}
                            {testResult.displayPhoneNumber &&
                              ` · ${testResult.displayPhoneNumber}`}
                            {testResult.qualityRating &&
                              ` · Quality: ${testResult.qualityRating}`}
                          </p>
                        ) : (
                          <p className="mt-0.5 text-xs text-danger">
                            {testResult.error}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <Button
                      loading={metaSaving}
                      onClick={() => void handleSaveMeta()}
                      size="sm"
                    >
                      Save
                    </Button>
                    <Button
                      disabled={
                        !metaConfig.phoneNumberId ||
                        (!draftToken.trim() && !metaConfig.hasAccessToken)
                      }
                      icon={<KeyRound className="size-4" />}
                      loading={metaTesting}
                      onClick={() => void handleTestMeta()}
                      size="sm"
                      variant="secondary"
                    >
                      Test
                    </Button>
                  </div>
                </>
              )}

              {provider !== "meta" && (
                <p className="text-sm text-muted">
                  Select Meta Cloud API above to configure official WhatsApp Business
                  messaging.
                </p>
              )}
            </CardBody>
          </Card>

          {/* ── Baileys card ────────────────────────────────────── */}
          <Card>
            <CardBody className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <MessageCircle
                    aria-hidden="true"
                    className="size-5 text-muted"
                    strokeWidth={1.75}
                  />
                  <span className="font-title text-base font-semibold text-ink">
                    Linked Phone
                  </span>
                </div>
                {baLoading && !baView ? (
                  <Skeleton className="h-5 w-20" />
                ) : baView ? (
                  <Badge tone={baView.tone}>{baView.label}</Badge>
                ) : null}
              </div>

              {baLoading && !baView ? (
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-full" />
                </div>
              ) : baView ? (
                <div>
                  <p className="text-sm font-medium text-ink">{baView.headline}</p>
                  <p className="mt-1 text-sm text-muted">{baView.detail}</p>
                </div>
              ) : null}

              {session?.phone && (
                <p className="flex items-center gap-2 text-sm text-ink">
                  <Smartphone aria-hidden="true" className="size-4 shrink-0 text-muted" />
                  Sending from{" "}
                  <span className="font-medium tabular-nums">{session.phone}</span>
                </p>
              )}

              {session?.status === "connecting" && (
                <div className="flex flex-col items-center gap-3 rounded-lg border border-rule bg-surface p-5">
                  {qr ? (
                    <img
                      alt="WhatsApp pairing QR code"
                      className="size-56 rounded-md bg-white p-2"
                      src={qr}
                    />
                  ) : (
                    <>
                      <Skeleton className="size-56 rounded-md" />
                      <p className="text-sm text-muted">Waiting for QR code…</p>
                    </>
                  )}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {session?.status === "connected" ? (
                  <Button
                    icon={<Link2Off aria-hidden="true" className="size-4" />}
                    onClick={() => setConfirmUnlink(true)}
                    variant="danger"
                    size="sm"
                  >
                    Unlink
                  </Button>
                ) : (
                  <Button
                    disabled={session?.status === "unreachable"}
                    loading={busy}
                    onClick={() => void startPairing()}
                    size="sm"
                  >
                    {session?.status === "connecting" ? "Restart pairing" : "Start pairing"}
                  </Button>
                )}
                <Button
                  icon={<RefreshCw aria-hidden="true" className="size-4" />}
                  loading={baLoading}
                  onClick={() => void refreshBaileys()}
                  size="sm"
                  variant="secondary"
                >
                  Refresh
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* ── Info banner ────────────────────────────────────────── */}
        <Alert kind="info" title="Platform-wide delivery">
          Enabled AiSensy campaigns handle their listed notification events. Other messages use the selected linked-phone or Meta provider. These settings serve all organizations.
        </Alert>

        {/* ── Unlink confirmation ────────────────────────────────── */}
        <ConfirmDialog
          confirmLabel="Unlink"
          loading={busy}
          message="Messages assigned to the linked-phone route will stop until a phone is linked again. Enabled AiSensy campaigns use their API connection."
          onClose={() => setConfirmUnlink(false)}
          onConfirm={() => void unlink()}
          open={confirmUnlink}
          title="Unlink WhatsApp?"
        />
      </div>
    </PageBody>
  );
}
